/**
 * Arkade Script Support
 *
 * This module provides encoding/decoding support for Arkade script opcodes
 * and PSBT fields. It reuses @scure/btc-signer for all Bitcoin standard
 * functionality and only adds Arkade-specific extensions.
 *
 * ## Features
 * - Standard Bitcoin opcodes via @scure/btc-signer (re-exported as OP)
 * - Arkade extension opcodes (0xb3, 0xc4-0xf3)
 * - Script encoding/decoding via ScriptElement arrays
 * - ASM format conversion with Arkade opcode support
 *
 * @module arkade
 */

// Re-export standard Bitcoin Script and opcodes from @scure/btc-signer
import { OP, Script, type ScriptType } from "@scure/btc-signer";
export { OP, Script, type ScriptType };

export {
    ARKADE_OP,
    ARKADE_OPCODES,
    ARKADE_OPCODE_NAMES,
    ARKADE_OPCODE_VALUES,
    OPCODE_NAMES,
    OPCODE_VALUES,
    getOpcodeName,
    getOpcodeValue,
} from "./opcodes";

// Export ArkadeScript CoderType (same API as @scure/btc-signer Script, with Arkade opcodes)
export { ArkadeScript, type ArkadeScriptType, type ArkadeScriptOP, ARKADE_OPS } from "./script";

export { toASM, fromASM, asmToBytes, bytesToASM } from "./script";
export { arkadeScriptHash, arkadeWitnessHash, computeArkadeScriptPublicKey } from "./tweak";
export * as BigNum from "./bignum";
export {
    createArkadeBatchHandler,
    type ArkadeBatchInput,
    type ArkadeExtendedCoin,
    type ArkadeExtendedVirtualCoin,
} from "./batch";
export {
    ArkadeProgramScript,
    stringifyArtifact,
    serializeArkadeContractParams,
    deserializeArkadeContractParams,
    witnessRefToBytes,
    type ArkadeContractParams,
    type CompiledProgramFunction,
    type ProgramKeys,
} from "./program";
export {
    Arkade,
    ArkadeContract,
    ArkadeTransactionBuilder,
    resolveAsm,
    parseArtifact,
    validateProgram,
    SUPPORTED_PROGRAM_VERSION,
    type Program,
    type ArkadeFunction,
    type AssetSpec,
    type TapscriptSegment,
    type ArkadeSegment,
    type AsmToken,
    type ArkadeParamValue,
    type ArkadeArgValue,
    type ArkadeArgType,
    type InputDef,
    type InputRef,
    type SignerRef,
    type WitnessRef,
    type Utxo,
    type ArkadeSpendResult,
    type CallableFunctions,
    type ContractFunctions,
    type ArkadeConnectOptions,
} from "./contract";
